import React, { useContext } from 'react';
import { RoomContext } from '../context/RoomContext';
import Room from './Room';
import BookForm from './BookForm';
import { isVisibleRoomCategory } from '../utils/roomFilters';

const RoomSkeleton = () => (
  <div className="bg-white shadow-2xl min-h-[500px] rounded-2xl border border-gray-200 animate-pulse">
    <div className="h-56 bg-gray-200 rounded-t-2xl" />
    <div className="h-12 bg-gray-100 mx-auto max-w-[300px] -translate-y-1/2 rounded" />
    <div className="px-6 pt-8 space-y-3">
      <div className="h-6 bg-gray-200 rounded mx-auto w-2/3" />
      <div className="h-4 bg-gray-100 rounded mx-auto w-full" />
      <div className="h-4 bg-gray-100 rounded mx-auto w-4/5" />
      <div className="h-10 bg-gray-200 rounded mx-auto w-2/3 mt-6" />
    </div>
  </div>
);

const Rooms = () => {
  const { rooms, roomsLoading, searchLoading, isSearchTriggered, totalGuests } = useContext(RoomContext);
  const visibleRooms = rooms.filter(
    (room) => isVisibleRoomCategory(room) && Number(room.maxPerson) === Number(totalGuests)
  );

  // Only 2 room categories (Deluxe/Suite) remain, so we usually render exactly 2 cards.
  // Size the grid to the actual card count so they stay centered instead of hugging the left
  // with empty space where a 3rd column used to be.
  const cardCount = roomsLoading && visibleRooms.length === 0 ? 2 : visibleRooms.length;
  const gridColumnsClass =
    cardCount >= 3
      ? 'lg:grid-cols-3 lg:max-w-none lg:mx-0'
      : cardCount === 2
      ? 'lg:grid-cols-2 lg:max-w-4xl lg:mx-auto'
      : 'lg:grid-cols-1 lg:max-w-sm lg:mx-auto';

  return (
    <section className="pt-8 lg:pt-20 pb-4 lg:pb-8" style={{ background: '#FFFFE7' }}>
      <div className="container mx-auto lg:px-0 rounded-3xl p-6 shadow-xl bg-white/80">
        <div className="flex flex-col items-center">
          <div className="text-center mb-6">
            <div className="font-tertiary uppercase text-[25px] tracking-[6px]">
              Hotel & Restaurant
            </div>
            <h2 className="font-primary text-[45px]">Room & Suites</h2>
          </div>

          <div className="w-full max-w-4xl">
            <BookForm />
          </div>
        </div>

        <div className="relative mt-8 min-h-[520px]">
          {searchLoading && visibleRooms.length > 0 && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex justify-center items-start pt-8 z-10 rounded-2xl pointer-events-none">
              <div className="text-gray-600 font-medium bg-white/90 px-4 py-2 rounded-full shadow-sm">
                Updating availability...
              </div>
            </div>
          )}

          <div
            className={`grid grid-cols-1 max-w-sm mx-auto gap-[30px] ${gridColumnsClass} transition-opacity duration-300 ${
              searchLoading && visibleRooms.length > 0 ? 'opacity-80' : 'opacity-100'
            }`}
          >
            {roomsLoading && visibleRooms.length === 0 ? (
              <>
                <RoomSkeleton />
                <RoomSkeleton />
              </>
            ) : Array.isArray(visibleRooms) && visibleRooms.length > 0 ? (
              visibleRooms.map((room) => (
                <Room room={room} key={room.id} canBook={isSearchTriggered} />
              ))
            ) : (
              <p className="text-center text-gray-500">
                {roomsLoading
                  ? 'Checking availability...'
                  : 'No rooms available for the selected dates and guests. Please try different dates.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Rooms;
